import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { StudioMark } from '../lib/branding.jsx';

/**
 * "WATCH ON TV" - pairing a TV app (Android TV / Samsung) with a gallery.
 *
 * The TV shows a 4-character code and a QR. Two ways to answer it:
 *
 *  - WatchOnTvSheet: from inside an unlocked web gallery (top bar button).
 *    The visitor's gallery cookie is the credential; they only type the TV code.
 *  - TvPairPage (/tv?code=K7M4): what the TV's QR opens on a phone. The TV
 *    code is pre-filled; the visitor adds their 8-character gallery code.
 */

const clean = (s, n) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, n);

export function WatchOnTvSheet({ slug, onClose }) {
  const [tvCode, setTvCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/gallery/${slug}/tv-pair`, { tvCode });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet stack" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <span className="label">Watch on TV</span>
        {done ? (
          <>
            <h2 className="serif" style={{ margin: 0 }}>Your TV is ready</h2>
            <p className="muted" style={{ margin: 0 }}>The gallery is opening on your TV now. It stays connected there.</p>
            <button type="button" className="primary" onClick={onClose}>Done</button>
          </>
        ) : (
          <>
            <p className="muted" style={{ margin: 0 }}>
              Open the gallery app on your Android TV or Samsung TV. Type the code it shows below.
            </p>
            <input
              autoFocus
              value={tvCode}
              onChange={(e) => setTvCode(clean(e.target.value, 4))}
              placeholder="K7M4"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck="false"
              className="tv-code-input"
              aria-label="TV code"
            />
            {error && <div className="error">{error}</div>}
            <button className="primary" disabled={busy || tvCode.length !== 4}>{busy ? 'Connecting…' : 'Show on my TV'}</button>
          </>
        )}
      </form>
    </div>
  );
}

export function TvPairPage() {
  const [params] = useSearchParams();
  const [tvCode, setTvCode] = useState(clean(params.get('code') || '', 4));
  const [galleryCode, setGalleryCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    document.title = 'Watch on TV';
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const out = await api.post('/api/client/v1/tv/pair', { tvCode, galleryCode });
      setTitle(out.title);
    } catch (err) {
      setError(err.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="unlock-screen">
      <div className="unlock-veil" />
      <form className="unlock-card" onSubmit={submit}>
        <div className="unlock-mark">
          <StudioMark />
        </div>
        <span className="label" style={{ marginBottom: '0.9rem' }}>Watch on TV</span>
        {title ? (
          <>
            <h1 className="serif unlock-name">{title}</h1>
            <div className="unlock-divider" />
            <p className="muted">Your gallery is opening on your TV now. It stays connected there.</p>
          </>
        ) : (
          <>
            <h1 className="serif unlock-name">Your memories. A bigger screen.</h1>
            <div className="unlock-divider" />
            <label htmlFor="tv">Code on your TV</label>
            <input
              id="tv"
              value={tvCode}
              onChange={(e) => setTvCode(clean(e.target.value, 4))}
              className="tv-code-input"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
            />
            <label htmlFor="gc" style={{ marginTop: '0.9rem' }}>Your 8-character gallery code</label>
            <input
              id="gc"
              autoFocus={tvCode.length === 4}
              value={galleryCode}
              onChange={(e) => setGalleryCode(clean(e.target.value, 8))}
              className="tv-code-input"
              placeholder="From your gallery invitation"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
            />
            {error && <div className="error" style={{ marginTop: '0.75rem' }}>{error}</div>}
            <button
              className="primary"
              disabled={busy || tvCode.length !== 4 || galleryCode.length !== 8}
              style={{ width: '100%', marginTop: '0.85rem' }}
            >
              {busy ? 'Connecting…' : 'Show on my TV'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
